"use client"
import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Navbar from "../../components/admin/common/Navbar"
import { getCampaignById, getRecentCampaigns } from "../../components/admin/marketing/campaignService"

export default function MarketingCampaignDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  // Campaign data state
  const [campaign, setCampaign] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  
  // Recent campaigns state
  const [recentCampaigns, setRecentCampaigns] = useState([])
  const [loadingRecent, setLoadingRecent] = useState(false)

  // Fetch campaign by ID
  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true)
      setError("")
      try {
        const response = await getCampaignById(id)
        // Response structure: { code: 200, message: "...", body: { _id, campaignName, ... } }
        const campaignData = response?.body
        
        if (campaignData) {
          setCampaign(campaignData)
        } else {
          setError("Campaign not found")
        }
      } catch (err) {
        console.error("Failed to load campaign:", err)
        setError("Failed to load campaign details")
      } finally {
        setLoading(false)
      }
    }
    
    if (id) {
      fetchCampaign()
    }
  }, [id])

  // Fetch recent campaigns
  useEffect(() => {
    const fetchRecent = async () => {
      setLoadingRecent(true)
      try {
        const response = await getRecentCampaigns()
        // Handle both array and nested response structures
        const campaigns = Array.isArray(response?.body) 
          ? response.body 
          : response?.body?.campaigns || response?.campaigns || []
        // Filter out current campaign and limit to 5
        const filtered = campaigns.filter(c => c._id !== id).slice(0, 5)
        setRecentCampaigns(filtered)
      } catch (err) {
        console.error("Failed to load recent campaigns:", err)
        setRecentCampaigns([])
      } finally {
        setLoadingRecent(false)
      }
    }
    
    fetchRecent()
  }, [id])

  // Get badges for campaign
  const getCampaignBadges = (campaign) => {
    const badges = []
    if (campaign?.isEmail) badges.push({ label: "Email", color: "bg-[#0d7ac2] text-white" })
    if (campaign?.isSms) badges.push({ label: "SMS", color: "bg-[#0d7ac2] text-white" })
    if (campaign?.status === "scheduled") badges.push({ label: "Scheduled", color: "bg-gray-300 text-gray-800" })
    if (campaign?.status === "draft") badges.push({ label: "Draft", color: "bg-gray-300 text-gray-800" })
    if (campaign?.status === "sent") badges.push({ label: "Sent", color: "bg-green-300 text-green-800" })
    if (campaign?.status === "in_progress") badges.push({ label: "In Progress", color: "bg-blue-300 text-blue-800" })
    return badges.length > 0 ? badges : [{ label: "Pending", color: "bg-gray-300 text-gray-800" }]
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="p-4 md:p-8 space-y-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="p-4 md:p-8 space-y-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error || "Campaign not found"}
          </div>
          <button
            onClick={() => navigate("/admin/marketing/campaigns")}
            className="text-[#0d7ac2] hover:underline"
          >
            &lt; Back to Campaigns
          </button>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="p-4 md:p-8 space-y-8">
        <div className="max-w-5xl">
          <button
            onClick={() => navigate("/admin/marketing/campaigns")}
            className="text-sm text-[#0d7ac2] hover:underline mb-4"
          >
            &lt; Back to Campaigns
          </button>
          
          <h2 className="text-xl md:text-2xl font-semibold mb-4">Recent Campaigns 
            <span className="text-gray-500 font-normal"> &gt; {campaign.campaignName || campaign.title || "N/A"}</span>
          </h2>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Left column: subject, type, group */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-2">Campaign Subject Line <span className="text-red-500">*</span></h3>
                  <input disabled value={campaign.campaignSubject || campaign.title || "N/A"} className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-2">Campaign Type</h3>
                  <div className="flex items-center gap-3 w-full border px-3 py-2 rounded-md">
                    <button disabled className={`px-3 py-1.5 rounded text-sm ${campaign.isEmail ? 'bg-[#0d7ac2] text-white' : 'bg-gray-200 text-gray-700'}`}>Email</button>
                    {/* <button disabled className={`px-3 py-1.5 rounded text-sm ${campaign.isSms ? 'bg-[#0d7ac2] text-white' : 'bg-gray-200 text-gray-700'}`}>SMS</button> */}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-1">Contact Group <span className="text-gray-400 font-normal">(based on visa type)</span></h3>
                  <input disabled value={campaign.contactGroup || "N/A"} className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50" />
                </div>
              </div>

              {/* Right column: email/sms body */}
              <div className="space-y-4">
                {campaign.isEmail && (
                  <div className="border rounded-md p-3 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-800 mb-2">Email Body</h3>
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 max-h-48 overflow-y-auto">{campaign.emailBody || "N/A"}</pre>
                  </div>
                )}
                {campaign.isSms && (
                  <div className="border rounded-md p-3 bg-gray-50">
                    <h3 className="text-sm font-medium text-gray-800 mb-2">SMS Body</h3>
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 max-h-48 overflow-y-auto">{campaign.smsBody || "N/A"}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Campaigns list */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">Recent Campaigns</h3>
              <button className="text-sm text-gray-600" onClick={() => navigate('/admin/marketing/campaigns')}>View More &gt;</button>
            </div>
            
            {loadingRecent ? (
              <div className="text-center py-8 text-gray-500">Loading recent campaigns...</div>
            ) : recentCampaigns.length > 0 ? (
              <div className="space-y-3">
                {recentCampaigns.map((c) => (
                  <div key={c._id || c.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between shadow-sm">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <p className="text-gray-900 font-medium">{c.campaignName || c.title || "Untitled Campaign"}</p>
                        <div className="flex items-center gap-2">
                          {getCampaignBadges(c).map((b, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 rounded ${b.color}`}>{b.label}</span>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">{formatDate(c.createdAt)} • {c.recipientCount || c.recipients || 0} recipients</p>
                    </div>
                    <button onClick={() => navigate(`/admin/marketing/campaigns/${c._id || c.id}`)} className="text-sm text-[#0d7ac2] hover:underline">View</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No other campaigns found</div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
